// ==============================================
// Admin Dashboard Page
// ==============================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UsersIcon,
  CubeIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

interface DrillTarget {
  packageId: string;
  packageName: string;
  band: ExpiryBand;
  label: string;
}

function SubscriberDrillModal({ target, onClose }: { target: DrillTarget; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'dashboard', 'expiring', target.packageId, target.band],
    queryFn: () => api.adminGetExpiringSubscribers({ packageId: target.packageId, band: target.band }),
  });

  const fmtDate = (iso: string | null) => {
    if (!iso) return <span className="text-slate-500">no end date</span>;
    const d = new Date(iso);
    const past = d.getTime() < Date.now();
    return <span className={past ? 'text-red-400' : 'text-slate-300'}>{d.toLocaleDateString('en-NZ')}{past ? ' (lapsed)' : ''}</span>;
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">{target.packageName}</h2>
            <p className="text-xs text-slate-400">{target.label}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XCircleIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {isLoading ? (
            <p className="text-slate-400 text-sm">Loading…</p>
          ) : (data?.length ?? 0) === 0 ? (
            <p className="text-slate-400 text-sm">No subscribers in this bucket.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-700">
                  <th className="pb-2 font-medium">Subscriber</th>
                  <th className="pb-2 font-medium text-right">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {data!.map((s) => (
                  <tr key={s.userId}>
                    <td className="py-2">
                      <p className="text-white">{s.name ?? s.email}</p>
                      {s.name && <p className="text-xs text-slate-400">{s.email}</p>}
                    </td>
                    <td className="py-2 text-right">{fmtDate(s.expiresAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

interface DashboardStats {
  users: {
    total: number;
    active: number;
    suspended: number;
    admins: number;
  };
  packages: {
    totalPackages: number;
    activePackages: number;
    totalSubscribers: number;
    revenueByPackage: { packageId: string; name: string; subscribers: number; monthlyRevenue: number }[];
  };
  monthlyRevenue: number;
  collectedLast30Days: number;
  recentOrders: any[];
  userGrowth: { date: string; count: number }[];
  packageExpiry: { packageId: string; name: string; activeTotal: number; expiring30: number; expiring60: number; expiring90: number; expiringBeyond: number }[];
}

type ExpiryBand = 'active' | '0_30' | '31_60' | '61_90' | '90_plus';

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'cyan' | 'green' | 'purple' | 'orange';
}) {
  const colorClasses = {
    cyan: 'from-cyan-500 to-blue-600 bg-cyan-500/10 text-cyan-400',
    green: 'from-green-500 to-emerald-600 bg-green-500/10 text-green-400',
    purple: 'from-purple-500 to-indigo-600 bg-purple-500/10 text-purple-400',
    orange: 'from-orange-500 to-red-600 bg-orange-500/10 text-orange-400',
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-400">{title}</p>
          <p className="text-3xl font-bold text-white mt-1">{value}</p>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color].split(' ').slice(2).join(' ')}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

export function AdminDashboardPage() {
  const { data: stats, isLoading, isError } = useQuery<DashboardStats>({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api.adminGetDashboard() as any,
  });
  const [drill, setDrill] = useState<DrillTarget | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <div className="bg-red-500/10 border border-red-500/50 rounded-xl p-6 text-red-400">
          <p className="font-medium">Failed to load dashboard data.</p>
          <p className="text-sm mt-2 text-slate-400">The admin dashboard API may not be fully migrated to Neon yet.</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-slate-400">Overview of your platform metrics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Users"
          value={stats?.users?.total ?? 0}
          subtitle={`${stats?.users?.active ?? 0} active`}
          icon={UsersIcon}
          color="cyan"
        />
        <StatCard
          title="Active Subscriptions"
          value={stats?.packages?.totalSubscribers ?? 0}
          subtitle={`${stats?.packages?.activePackages ?? 0} packages`}
          icon={CubeIcon}
          color="green"
        />
        <StatCard
          title="Monthly Revenue (MRR)"
          value={formatCurrency(stats?.monthlyRevenue ?? 0)}
          subtitle={`${formatCurrency(stats?.collectedLast30Days ?? 0)} collected · last 30d`}
          icon={CurrencyDollarIcon}
          color="purple"
        />
        <StatCard
          title="Recent Orders"
          value={stats?.recentOrders?.length ?? 0}
          subtitle="Last 30 days"
          icon={ShoppingCartIcon}
          color="orange"
        />
      </div>

      {/* Active Subscribers by Package — active subs banded by term end */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-1">Active Subscribers by Package</h3>
        <p className="text-xs text-slate-500 mb-4">Active subscriptions banded by when their current term ends (current_period_end). Click any number to see the subscribers.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-3 font-medium">Package</th>
                <th className="pb-3 font-medium text-right">Active</th>
                <th className="pb-3 font-medium text-right">Expiring 0–30d</th>
                <th className="pb-3 font-medium text-right">31–60d</th>
                <th className="pb-3 font-medium text-right">61–90d</th>
                <th className="pb-3 font-medium text-right">&gt;90d</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {(stats?.packageExpiry?.length ?? 0) === 0 ? (
                <tr><td colSpan={6} className="py-4 text-center text-slate-400">No active subscribers</td></tr>
              ) : (
                stats!.packageExpiry.map((row) => {
                  const cell = (band: ExpiryBand, value: number, label: string, tone: string) => (
                    <td className="py-2.5 text-right">
                      <button
                        type="button"
                        disabled={value === 0}
                        onClick={() => setDrill({ packageId: row.packageId, packageName: row.name, band, label })}
                        className={`${value === 0 ? 'text-slate-600 cursor-default' : `${tone} hover:underline`}`}
                      >
                        {value}
                      </button>
                    </td>
                  );
                  return (
                    <tr key={row.packageId} className="hover:bg-slate-700/20">
                      <td className="py-2.5 text-white">{row.name}</td>
                      {cell('active', row.activeTotal, 'All active subscribers', 'text-slate-300')}
                      {cell('0_30', row.expiring30, 'Term ends in 0–30 days', 'text-orange-400')}
                      {cell('31_60', row.expiring60, 'Term ends in 31–60 days', 'text-amber-300')}
                      {cell('61_90', row.expiring90, 'Term ends in 61–90 days', 'text-slate-300')}
                      {cell('90_plus', row.expiringBeyond, 'Term ends in >90 days (or lapsed / no end set)', 'text-slate-300')}
                    </tr>
                  );
                })
              )}
            </tbody>
            {(stats?.packageExpiry?.length ?? 0) > 0 && (
              <tfoot>
                <tr className="border-t border-slate-700 font-medium text-white">
                  <td className="py-2.5">Total</td>
                  <td className="py-2.5 text-right">{stats!.packageExpiry.reduce((s, r) => s + r.activeTotal, 0)}</td>
                  <td className="py-2.5 text-right">{stats!.packageExpiry.reduce((s, r) => s + r.expiring30, 0)}</td>
                  <td className="py-2.5 text-right">{stats!.packageExpiry.reduce((s, r) => s + r.expiring60, 0)}</td>
                  <td className="py-2.5 text-right">{stats!.packageExpiry.reduce((s, r) => s + r.expiring90, 0)}</td>
                  <td className="py-2.5 text-right">{stats!.packageExpiry.reduce((s, r) => s + r.expiringBeyond, 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Revenue by Package */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Revenue by Package</h3>
          <div className="space-y-3">
            {stats?.packages?.revenueByPackage?.map((pkg) => (
              <div key={pkg.packageId} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">{pkg.name}</p>
                  <p className="text-xs text-slate-400">{pkg.subscribers} subscribers</p>
                </div>
                <p className="text-sm font-medium text-green-400">
                  {formatCurrency(pkg.monthlyRevenue)}
                </p>
              </div>
            )) || (
              <p className="text-slate-400 text-sm">No subscription data available</p>
            )}
          </div>
        </div>

        {/* User Growth */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">User Growth</h3>
          <div className="space-y-2">
            {stats?.userGrowth?.map((month) => (
              <div key={month.date} className="flex items-center justify-between">
                <p className="text-sm text-slate-400">{month.date}</p>
                <div className="flex items-center gap-2">
                  <div
                    className="h-2 bg-cyan-500 rounded"
                    style={{ width: `${Math.min(month.count * 10, 200)}px` }}
                  />
                  <p className="text-sm font-medium text-white">{month.count}</p>
                </div>
              </div>
            )) || (
              <p className="text-slate-400 text-sm">No growth data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Orders</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-slate-400 border-b border-slate-700">
                <th className="pb-3 font-medium">Order ID</th>
                <th className="pb-3 font-medium">User</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Tokens</th>
                <th className="pb-3 font-medium">Amount</th>
                <th className="pb-3 font-medium">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {stats?.recentOrders?.map((order) => (
                <tr key={order.id} className="text-sm">
                  <td className="py-3 font-mono text-slate-300">{order.id.slice(0, 8)}...</td>
                  <td className="py-3 text-white">{order.user?.email || 'Unknown'}</td>
                  <td className="py-3">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        order.status === 'completed'
                          ? 'bg-green-500/20 text-green-400'
                          : order.status === 'processing'
                          ? 'bg-blue-500/20 text-blue-400'
                          : order.status === 'failed'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-slate-500/20 text-slate-400'
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="py-3 text-slate-300">{order.tile_count?.toLocaleString()}</td>
                  <td className="py-3 text-slate-300">{formatCurrency(order.total_cents)}</td>
                  <td className="py-3 text-slate-400">
                    {new Date(order.created_at).toLocaleDateString('en-NZ')}
                  </td>
                </tr>
              )) || (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-slate-400">
                    No recent orders
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {drill && <SubscriberDrillModal target={drill} onClose={() => setDrill(null)} />}
    </div>
  );
}

export default AdminDashboardPage;
